import { Controller3D } from "./3DController";
import { Mesh, Object3D } from "three"
import { clamp, lerp, sleep } from "../ulti";
import { CarModelCameraList, CarModelConfig, ModelLoadController } from "./ModelLoad/ModelLoadController";

const HEIGHT_MIN:number = 1.5;
const HEIGHT_MAX:number = 2.0;

const WEIGHT_MIN:number = 45;
const WEIGHT_MAX:number = 120;

interface IOccupantLocation {
  grouping:string;
  mesh:string;
  attach?:string;
  awaitOnEnter?:boolean;
  onEnter:(forward:boolean)=>Promise<void>;
  onLeave:(forward:boolean, exit:boolean)=>void;
}

export class GenericPassengerController {
  public static HEIGHT_MIN:number = HEIGHT_MIN;
  public static HEIGHT_MAX:number = HEIGHT_MAX;

  public static WEIGHT_MIN:number = WEIGHT_MIN;
  public static WEIGHT_MAX:number = WEIGHT_MAX;

  private _active:boolean = false;
  private _height:number = 1.75;
  private _weight:number = 70;
  private blocked:boolean = false;

  private animationToken:any|null;
  private animationTokenTimeout:any = -1;

  public get active():boolean { return this._active; }

  public get height():number { return this._height; }
  public set height(value:number) {
    if (this.height != value) {
      this._height = value;
      this.updateMorphTargets();
    }
  }

  public get weight():number { return this._weight; }
  public set weight(value:number) {
    if (this.weight != value) {
      this._weight = value;
      this.updateMorphTargets();
    }
  }

  private occupantGroupID:string;
  private occupantOriginalParent:Object3D;
  private occupant:Mesh;

  private currentLocationID:number;
  private locations:IOccupantLocation[];

  public constructor(private controller3D:Controller3D) {
    ModelLoadController.event.on(ModelLoadController.STATUS_LOADED, (config:CarModelConfig) => {
      this.createLocations(config);
    });
  }

  public async enter():Promise<void> {
    if (this._active) throw 'GenericPassenger: cannot enter twice';

    this._active = true;
    this.changeLocation(0, true, true);
  }

  public async leave():Promise<void> {
    if (!this._active) throw 'GenericPassenger: cannot leave before enter';

    if (this.blocked) {
      return;
    }

    this._active = false;

    await this.leaveCurrentLocation(false, true);
    this.controller3D.setCamera(CarModelCameraList.Orbit);

    this.currentLocationID = null;
  }

  public locationRotateLeft():void {
    if (!this._active) throw 'GenericPassenger: cannot rotate before enter';

    const locationCount = this.locations.length;

    this.changeLocation((this.currentLocationID + locationCount - 1) % locationCount, false);
  }

  public locationRotateRight():void {
    if (!this._active) throw 'GenericPassenger: cannot rotate before enter';

    this.changeLocation((this.currentLocationID + 1) % this.locations.length, true);
  }

  public async changeLocation(locationID:number, forward:boolean, first?:boolean):Promise<void> {
    if (this.blocked || locationID == this.currentLocationID) {
      return;
    }

    const location = this.locations[locationID];

    this.blocked = true;

    if (!first) {
      await this.leaveCurrentLocation(forward, false);
    }

    this.currentLocationID = locationID;

    if (location.awaitOnEnter) {
      await location.onEnter(forward);
    }

    const configurator = this.controller3D.engine.configurator;
    const group = configurator.config.groupings.find(
      g => g.name === location.grouping);
    if (!group) {
      throw `Cannot find group: ${location.grouping}`;
    }

    this.occupantGroupID = group.id;
    await configurator.setVisible(this.occupantGroupID, true);

    const occupant = this.controller3D.engine.scene.getObjectByName(location.mesh);

    if (occupant instanceof Mesh && !Array.isArray(occupant.material)) {
      occupant.material.morphTargets = true;
      occupant.material.morphNormals = true;
    } else {
      throw 'Cannot enable morph targets on occupant.'
    }

    this.occupantOriginalParent = occupant.parent;

    if (location.attach) {
      const attachNode = this.controller3D.engine.scene.getObjectByName(location.attach);

      if (attachNode) {
        attachNode.add(occupant);
        if (this.occupantOriginalParent) {
          attachNode.scale.copy(this.occupantOriginalParent.scale);
        }
      } else {
        throw `Cannot find attach node: ${location.attach}`
      }
    }
    
    this.occupant = occupant;
    this.updateMorphTargets();

    if (!location.awaitOnEnter) {
      await location.onEnter(forward);
    }

    this.blocked = false;
  }

  private async leaveCurrentLocation(forward:boolean, exit:boolean):Promise<void> {
    await this.locations[this.currentLocationID].onLeave(forward, exit);
    if (this.occupantOriginalParent) {
      this.occupantOriginalParent.add(this.occupant);
    }
    this.controller3D.engine.configurator.setVisible(this.occupantGroupID, false);
  }

  private updateMorphTargets():void {
    if (!this._active) {
      return;
    }

    const height = clamp(this.height, HEIGHT_MIN, HEIGHT_MAX);
    const weight = clamp(this.weight, WEIGHT_MIN, WEIGHT_MAX);

    const occupant = this.occupant;
    const heightMorphID = occupant.morphTargetDictionary["height"];
    const weightMorphID = occupant.morphTargetDictionary["weight"];

    if (heightMorphID === undefined) {
      throw 'Missing height morph target';
    }

    if (weightMorphID === undefined) {
      throw 'Missing weight morph target';
    }

    let heightFactor = (height - HEIGHT_MIN) / (HEIGHT_MAX - HEIGHT_MIN);
    let weightFactor = (weight - WEIGHT_MIN) / (WEIGHT_MAX - WEIGHT_MIN);

    const rangeMin = lerp(0.25, -0.25, heightFactor);
    const rangeMax = lerp(1.5, 1.0, heightFactor); 

    weightFactor *= rangeMax - rangeMin;
    weightFactor += rangeMin;

    if (this.animationToken == null) {
      this.animationToken = this.controller3D.animationController.acquireToken();
    } else {
      clearInterval(this.animationTokenTimeout);
    }

    occupant.morphTargetInfluences[heightMorphID] = heightFactor;
    occupant.morphTargetInfluences[weightMorphID] = weightFactor;

    this.animationTokenTimeout = setTimeout(() => {
      this.controller3D.animationController.releaseToken(this.animationToken);
      this.animationToken = null;
    }, 200);
  }

  private createLocations(config:CarModelConfig):void {
    this.locations = [];

    // Standing
    this.locations.push(
      {
        grouping: "Occupant Standing",
        mesh: "Occupant_Standing",
        attach: "OCCUPANT",
        awaitOnEnter: true,
        onEnter: async () => {
          this.controller3D.setCamera(CarModelCameraList.PassengerStanding);
  
          if (this.controller3D.carOpen) {
            await this.controller3D.playAnimation();
          }
        },
        onLeave: async () => {}
      }
    );

    // FL (exterior)
    this.locations.push(
      {
        grouping: "Occupant Interior",
        mesh: "Occupant_Interior",
        attach: "OCCUPANT_FL",
        onEnter: async () => {
          this.controller3D.setCamera(CarModelCameraList.PassengerIntFL);
  
          if (!this.controller3D.doorFrontLeftOpen) {
            await this.controller3D.playFrontLeftDoorAnimation();
          }
        },
        onLeave: async () => {
          if (this.controller3D.doorFrontLeftOpen) {
            await this.controller3D.playFrontLeftDoorAnimation();
          }
        }
      }
    );

    if (config.features.haveBackDoors) {
      // RL
      this.locations.push(
        {
          grouping: "Occupant Interior",
          mesh: "Occupant_Interior",
          attach: "OCCUPANT_RL",
          onEnter: async () => {
            this.controller3D.setCamera(CarModelCameraList.PassengerIntRL);
    
            if (!this.controller3D.doorRearLeftOpen) {
              await this.controller3D.playRearLeftDoorAnimation();
            }
          },
          onLeave: async () => {
            if (this.controller3D.doorRearLeftOpen) {
              this.controller3D.playRearLeftDoorAnimation();
            }
          }
        }
      );

      // RR
      this.locations.push(
        {
          grouping: "Occupant Interior",
          mesh: "Occupant_Interior",
          attach: "OCCUPANT_RR",
          onEnter: async () => {
            this.controller3D.setCamera(CarModelCameraList.PassengerIntRR);
    
            if (!this.controller3D.doorRearRightOpen) {
              await this.controller3D.playRearRightDoorAnimation();
            }
          },
          onLeave: async () => {
            if (this.controller3D.doorRearRightOpen) {
              this.controller3D.playRearRightDoorAnimation();
            }
          }
        }
      );
    }

    // FR
    this.locations.push({
      grouping: "Occupant Interior",
      mesh: "Occupant_Interior",
      attach: "OCCUPANT_FR",
      onEnter: async (forward:boolean) => {
        this.controller3D.setCamera(CarModelCameraList.PassengerIntFR);

        if (forward && !this.controller3D.doorFrontRightOpen) {
          await this.controller3D.playFrontRightDoorAnimation();
        }
      },
      onLeave: async (forward:boolean) => {
        if (!forward && this.controller3D.doorFrontRightOpen) {
          this.controller3D.playFrontRightDoorAnimation();
        }
      }
    });

    // FL (interior)
    this.locations.push({
      grouping: "Occupant Interior",
      mesh: "Occupant_Interior",
      attach: "OCCUPANT_FL",
      onEnter: async (forward:boolean) => {
        if (!forward && !this.controller3D.doorFrontRightOpen) {
          this.controller3D.playFrontRightDoorAnimation();
          await sleep(600);
        }

        await this.controller3D.setCamera(CarModelCameraList.PassengerIntDriverInOut);

        this.controller3D.setCamera(CarModelCameraList.PassengerIntDriver);
      },
      onLeave: async (forward:boolean, exit:boolean) => {
        // if (forward || exit) {
          if (!this.controller3D.doorFrontRightOpen) {
            this.controller3D.playFrontRightDoorAnimation();
            await sleep(600);
          }
          
          await this.controller3D.setCamera(CarModelCameraList.PassengerIntDriverInOut);

          if ((forward || exit) && this.controller3D.doorFrontRightOpen) {
            this.controller3D.playFrontRightDoorAnimation();
          }
        // }
      }
    });
  }
}